/**
 * Tracks text-search filter inputs whose value has changed but has not yet
 * been committed to the filter state (text filters debounce before firing
 * onValuesChange).
 *
 * Why: while a text filter's debounce is pending, the list still renders the
 * PREVIOUS search's settled state. When that state was empty, the user sees a
 * stale "No data available" the whole time they're typing — reading as "my
 * search found nothing" even though the new search hasn't run yet (PD-14404).
 * List surfaces subscribe here so they can show the loading skeleton instead
 * while a new search is pending.
 *
 * Keyed by listKey: a text filter announces itself under every listKey in its
 * FilterConfig.listKeys, and each list surface asks about its own listKey.
 */
class TextSearchPendingServiceClass {
	private readonly _pendingByListKey = new Map<string, Set<string>>();
	private readonly _listenersByListKey = new Map<string, Set<() => void>>();

	public setPending(
		listKeys: Array<string>,
		filterType: string,
		pending: boolean,
	): void {
		for (const listKey of listKeys) {
			const pendingSet = this._pendingByListKey.get(listKey);

			if (pending) {
				if (pendingSet == null) {
					this._pendingByListKey.set(listKey, new Set([filterType]));
					this._notify(listKey);
				} else if (!pendingSet.has(filterType)) {
					pendingSet.add(filterType);
					this._notify(listKey);
				}
			} else if (pendingSet != null && pendingSet.has(filterType)) {
				pendingSet.delete(filterType);

				if (pendingSet.size === 0) {
					this._pendingByListKey.delete(listKey);
				}

				this._notify(listKey);
			}
		}
	}

	public isPending(listKey: string): boolean {
		return (this._pendingByListKey.get(listKey)?.size ?? 0) > 0;
	}

	public subscribe(listKey: string, listener: () => void): () => void {
		let listeners = this._listenersByListKey.get(listKey);

		if (listeners == null) {
			listeners = new Set();
			this._listenersByListKey.set(listKey, listeners);
		}

		listeners.add(listener);

		return () => {
			const current = this._listenersByListKey.get(listKey);

			if (current != null) {
				current.delete(listener);

				if (current.size === 0) {
					this._listenersByListKey.delete(listKey);
				}
			}
		};
	}

	private _notify(listKey: string): void {
		this._listenersByListKey.get(listKey)?.forEach((listener) => {
			listener();
		});
	}
}

export const textSearchPendingService = new TextSearchPendingServiceClass();
